// TRIL-313 / TRIL-266: canonical text-normalization rule used to decide whether
// an edit is substantive enough to invalidate a story analysis. This is the
// SINGLE SOURCE OF TRUTH for the rule — TRIL-315 (server signature verify) and
// STYZER-63 (analyzer signing) must mirror it exactly so all three layers agree
// on "what counts as a content change".
//
// Design (TRIL-266): the analysis gate also backstops safety + integrity, so the
// rule must FAIL SAFE — only treat unambiguously cosmetic differences as "no
// change". We collapse whitespace and strip sentence-terminal punctuation, but
// deliberately preserve case, words, and meaning-bearing punctuation (commas,
// colons, quotes, decimals) because those can change meaning.

export const normalizeStoryText = (text: string): string =>
	text
		.normalize("NFC")
		// Strip sentence-terminal . ! ? … (each immediately followed by whitespace or
		// end-of-text) anywhere in the text — the common "removed a period" edit,
		// mid-sentence or trailing. A '.' before a digit/letter (decimals like
		// "3.14", abbreviations) is NOT matched, so it's preserved; commas and other
		// punctuation are preserved too (so removing the comma in "eat, Grandma"
		// still triggers re-analysis).
		.replace(/[.!?…]+(?=\s|$)/gu, "")
		.replace(/\s+/g, " ")
		.trim();

/**
 * True when an edit should invalidate the current analysis (i.e. re-analyze).
 * Cosmetic-only edits (whitespace, trailing punctuation) return false; any word
 * or internal-punctuation change returns true. With no prior snapshot we cannot
 * tell, so we fail safe and re-analyze.
 */
export const shouldReanalyzeOnEdit = (
	currentText: string,
	lastAnalyzedText: string | null | undefined,
): boolean => {
	if (lastAnalyzedText == null) return true;
	return (
		normalizeStoryText(currentText) !== normalizeStoryText(lastAnalyzedText)
	);
};
